import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    Bar, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from '../team-report/KeyTakeaway';
import VizToggle, { VizType } from '../team-report/VizToggle';
import { SlideTypeDurationRow, ReportSection, formatDuration } from './types';

const TYPE_COLORS: Record<string, string> = {
    activity: '#4caf50',
    worksheet: '#2196f3',
    conversation: '#ff9800',
};

interface Props {
    data: ReportSection<SlideTypeDurationRow>;
}

const SlideTypeSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>('bar');
    const theme = useTheme();

    const chartData = data.rows.map((r) => ({
        type: r.slide_type.charAt(0).toUpperCase() + r.slide_type.slice(1),
        avg: Math.round(r.avg_seconds / 60 * 10) / 10,
        median: Math.round(r.median_seconds / 60 * 10) / 10,
        fill: TYPE_COLORS[r.slide_type] || theme.palette.primary.main,
    }));

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-slide-type">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">3. Slide Type Comparison</Typography>
                <VizToggle value={viz} options={['bar', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Average duration grouped by slide format. Which type drives the most engagement?
            </Typography>

            {viz === 'bar' && (
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                        <YAxis label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(val: number, name: string) => [`${val} min`, name]} />
                        <Bar dataKey="avg" name="Average" radius={[4, 4, 0, 0]}>
                            {chartData.map((entry, idx) => (
                                <Cell key={idx} fill={entry.fill} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Avg</TableCell>
                                <TableCell align="right">Median</TableCell>
                                <TableCell align="right">Min</TableCell>
                                <TableCell align="right">Max</TableCell>
                                <TableCell align="right">Completed</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={r.slide_type}>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>{r.slide_type}</TableCell>
                                    <TableCell align="right">{formatDuration(r.avg_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.median_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.min_seconds)}</TableCell>
                                    <TableCell align="right">{formatDuration(r.max_seconds)}</TableCell>
                                    <TableCell align="right">{r.completion_count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default SlideTypeSection;
